import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Ticket, Utensils, Trophy, Lightbulb, Sparkles, CalendarDays } from 'lucide-react';
import { buildPlannerHref } from '../utils/plannerLink';

const ALLOWED_IMAGE_HOSTS = [
  'unsplash.com',
  'images.unsplash.com',
  'cdn.unsplash.com',
  'lh3.googleusercontent.com',
];
const DURATIONS = [3, 5, 7];

function isSafeImageUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const u = new URL(url);
    if (u.protocol !== 'https:') return false;
    return ALLOWED_IMAGE_HOSTS.some((h) => u.hostname === h || u.hostname.endsWith(`.${h}`));
  } catch {
    return false;
  }
}

function buildMapUrl(placeName, city) {
  const q = [placeName, city].filter(Boolean).join(' ');
  if (!q) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

function isSafeGoogleMapsUrl(url) {
  if (!url || typeof url !== 'string') return false;
  try {
    const u = url.trim();
    return u.startsWith('https://www.google.com/maps') && !u.includes('goo.gl') && !u.includes('maps.app.goo.gl');
  } catch {
    return false;
  }
}

function slotHeadline(slot) {
  if (!Array.isArray(slot) || !slot.length) return null;
  const first = slot[0];
  return typeof first === 'string' ? first : first?.name || null;
}

function daySummaryLine(day, mapCity) {
  const a = slotHeadline(day.morning);
  const b = slotHeadline(day.afternoon);
  const c = slotHeadline(day.evening);
  const parts = [a, b, c].filter(Boolean).slice(0, 2);
  if (parts.length) return parts.join(' · ');
  return `Plan your day around ${mapCity}`;
}

function ActivityCard({ activity, city }) {
  const builtMapUrl = activity.placeName ? buildMapUrl(activity.placeName, city) : null;
  const mapUrl = isSafeGoogleMapsUrl(activity.mapLink) ? activity.mapLink : builtMapUrl;
  const showImage = isSafeImageUrl(activity.imageUrl);

  return (
    <div className="flex flex-col sm:flex-row gap-3 p-4 rounded-xl bg-surface-container-low dark:bg-slate-800/90 border border-outline-variant/20 dark:border-slate-700">
      <div className="sm:w-32 flex-shrink-0">
        {showImage ? (
          <img
            src={activity.imageUrl}
            alt=""
            className="w-full h-24 object-cover rounded-lg"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling?.classList.remove('hidden');
            }}
          />
        ) : null}
        <div
          className={`w-full h-24 rounded-lg bg-surface-container-high dark:bg-slate-700 flex items-center justify-center ${showImage ? 'hidden' : ''}`}
          aria-hidden="true"
        >
          {mapUrl ? (
            <a
              href={mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center justify-center gap-0.5 text-on-surface-variant dark:text-slate-300 hover:text-primary dark:hover:text-primary-container w-full h-full rounded-lg"
            >
              <MapPin className="h-8 w-8" />
              <span className="text-xs">Map</span>
            </a>
          ) : (
            <span className="text-3xl text-on-surface-variant/50" aria-hidden>
              ·
            </span>
          )}
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-on-surface dark:text-white font-headline">
          {activity.suggested_time && (
            <span className="text-primary dark:text-primary-container font-mono mr-2 text-sm">{activity.suggested_time}</span>
          )}
          {activity.name}
        </h4>
        {activity.description && <p className="text-sm text-on-surface-variant dark:text-slate-400 mt-1">{activity.description}</p>}
        <div className="flex flex-wrap gap-2 mt-2">
          {mapUrl && (
            <a
              href={mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary dark:text-primary-container font-medium hover:underline"
            >
              <MapPin className="h-4 w-4" />
              View on map
            </a>
          )}
          {activity.ticketLink && activity.ticketLink.startsWith('https') && (
            <a
              href={activity.ticketLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary dark:text-primary-container font-medium hover:underline"
            >
              <Ticket className="h-4 w-4" />
              Get tickets
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function SlotSection({ title, activities, city }) {
  if (!activities?.length) return null;
  return (
    <div>
      <h4 className="text-xs font-bold text-on-surface-variant dark:text-slate-500 uppercase tracking-wider mb-2">{title}</h4>
      <div className="space-y-2">
        {activities.map((a, i) => (
          <ActivityCard key={i} activity={typeof a === 'string' ? { name: a } : a} city={city} />
        ))}
      </div>
    </div>
  );
}

/**
 * @param {{ guideCityName: string, plansByDuration: Record<string, object> }} props
 */
export function PrebuiltItineraryBlog({ guideCityName, plansByDuration }) {
  const [duration, setDuration] = useState(3);
  const plan = plansByDuration?.[String(duration)] || plansByDuration?.[duration];
  const mapCity = plan?.plannerCity || plan?.city || guideCityName;

  const plannerHref = useMemo(() => {
    if (!plan) return '/itinerary-planner';
    return buildPlannerHref({
      city: plan.plannerCity,
      days: duration,
      matchDay: plan.matchDay,
      budget: 'medium',
      transport: 'public_transport',
    });
  }, [plan, duration]);

  if (!plan?.itinerary?.length) return null;

  const tabId = (d) => `itinerary-tab-${d}`;
  const panelId = 'itinerary-day-panel';

  return (
    <section className="not-prose" aria-labelledby="prebuilt-itineraries-heading">
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div className="flex items-start gap-3 min-w-0">
          <CalendarDays className="h-8 w-8 text-tertiary shrink-0 mt-1" aria-hidden />
          <div>
            <h2 id="prebuilt-itineraries-heading" className="text-2xl sm:text-3xl font-black text-on-surface dark:text-white font-headline tracking-tight">
              Sample AI itineraries for {guideCityName}
            </h2>
            <p className="text-base font-semibold text-primary dark:text-primary-container mt-2">
              Choose your trip length: 3, 5, or 7 days
            </p>
            <p className="text-sm text-on-surface-variant dark:text-slate-400 mt-2 max-w-2xl leading-relaxed">
              Day-by-day plans for the World Cup venue area ({mapCity}). Switch tabs, then open the planner to regenerate with your match day and budget.
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-8" role="tablist" aria-label="Trip length">
        {DURATIONS.map((d) => (
          <button
            key={d}
            id={tabId(d)}
            type="button"
            role="tab"
            aria-selected={duration === d}
            aria-controls={panelId}
            tabIndex={duration === d ? 0 : -1}
            onClick={() => setDuration(d)}
            className={`px-5 py-2.5 rounded-full text-sm font-black transition-all ${
              duration === d
                ? 'bg-primary text-on-primary shadow-fanhub-tint scale-[1.02]'
                : 'bg-surface-container-high dark:bg-slate-800 text-on-surface dark:text-slate-200 hover:bg-primary-container/40 dark:hover:bg-slate-700 border border-outline-variant/20 dark:border-slate-600'
            }`}
          >
            {d} days
          </button>
        ))}
      </div>

      <div id={panelId} role="tabpanel" aria-labelledby={tabId(duration)} tabIndex={0} className="outline-none">
        <div className="grid lg:grid-cols-3 gap-6 mb-10">
          {Array.isArray(plan.top_food_recommendations) && plan.top_food_recommendations.length > 0 && (
            <div className="p-4 rounded-2xl bg-surface-container-lowest dark:bg-slate-900/80 border border-outline-variant/15 dark:border-slate-700">
              <h3 className="text-sm font-bold text-on-surface dark:text-white flex items-center gap-2 mb-2 font-headline">
                <Utensils className="h-4 w-4 text-primary shrink-0" aria-hidden />
                Food picks
              </h3>
              <ul className="text-sm text-on-surface-variant dark:text-slate-400 space-y-1 list-disc pl-4">
                {plan.top_food_recommendations.map((item, i) => (
                  <li key={i}>{typeof item === 'string' ? item : item?.name || JSON.stringify(item)}</li>
                ))}
              </ul>
            </div>
          )}
          {Array.isArray(plan.football_experiences) && plan.football_experiences.length > 0 && (
            <div className="p-4 rounded-2xl bg-surface-container-lowest dark:bg-slate-900/80 border border-outline-variant/15 dark:border-slate-700">
              <h3 className="text-sm font-bold text-on-surface dark:text-white flex items-center gap-2 mb-2 font-headline">
                <Trophy className="h-4 w-4 text-primary shrink-0" aria-hidden />
                Football experiences
              </h3>
              <ul className="text-sm text-on-surface-variant dark:text-slate-400 space-y-1 list-disc pl-4">
                {plan.football_experiences.map((item, i) => (
                  <li key={i}>{typeof item === 'string' ? item : item?.name || JSON.stringify(item)}</li>
                ))}
              </ul>
            </div>
          )}
          {Array.isArray(plan.travel_tips) && plan.travel_tips.length > 0 && (
            <div className="p-4 rounded-2xl bg-surface-container-lowest dark:bg-slate-900/80 border border-outline-variant/15 dark:border-slate-700 lg:col-span-1">
              <h3 className="text-sm font-bold text-on-surface dark:text-white flex items-center gap-2 mb-2 font-headline">
                <Lightbulb className="h-4 w-4 text-primary shrink-0" aria-hidden />
                Travel tips
              </h3>
              <ul className="text-sm text-on-surface-variant dark:text-slate-400 space-y-1 list-disc pl-4">
                {plan.travel_tips.map((item, i) => (
                  <li key={i}>{typeof item === 'string' ? item : item?.name || JSON.stringify(item)}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <h3 className="text-lg font-black text-on-surface dark:text-white mb-4 font-headline tracking-tight">
          {duration}-day snapshot
        </h3>
        <div className="space-y-3 mb-10">
          {plan.itinerary.map((day) => (
            <div
              key={`sum-${day.day}`}
              className="grid grid-cols-1 md:grid-cols-[100px_1fr] gap-4 items-center p-5 bg-surface-container-low dark:bg-slate-800/70 rounded-2xl border border-outline-variant/15 dark:border-slate-700"
            >
              <div className="text-center md:text-left">
                <div className="text-xs uppercase font-bold text-on-surface-variant">Day</div>
                <div className="text-4xl font-black text-primary dark:text-primary-container font-headline leading-none">
                  {String(day.day).padStart(2, '0')}
                </div>
              </div>
              <div>
                <p className="text-sm text-on-surface-variant dark:text-slate-300 leading-relaxed">{daySummaryLine(day, mapCity)}</p>
              </div>
            </div>
          ))}
        </div>

        <h3 className="text-lg font-black text-on-surface dark:text-white mb-4 font-headline tracking-tight">Hour-by-hour</h3>
        <div className="space-y-8">
          {plan.itinerary.map((day) => (
            <article
              key={day.day}
              className="p-5 sm:p-6 rounded-2xl bg-surface-container-lowest dark:bg-slate-900/85 border border-outline-variant/15 dark:border-slate-700 shadow-sm"
            >
              <h4 className="text-xl font-black text-on-surface dark:text-white mb-4 border-b border-outline-variant/25 dark:border-slate-600 pb-2 font-headline">
                Day {day.day}
              </h4>
              <div className="space-y-6">
                <SlotSection title="Morning" activities={day.morning} city={mapCity} />
                <SlotSection title="Afternoon" activities={day.afternoon} city={mapCity} />
                <SlotSection title="Evening" activities={day.evening} city={mapCity} />
              </div>
            </article>
          ))}
        </div>

        <div className="mt-8 p-6 rounded-2xl bg-primary-container/25 dark:bg-primary-dim/20 border border-primary/20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-3">
            <Sparkles className="h-6 w-6 text-primary shrink-0 mt-0.5" aria-hidden />
            <div>
              <p className="font-bold text-on-surface dark:text-white font-headline">Customize this {duration}-day plan</p>
              <p className="text-sm text-on-surface-variant dark:text-slate-400 mt-1">
                Same venue focus ({mapCity}), editable match day, budget, and interests in the AI planner.
              </p>
            </div>
          </div>
          <Link
            to={plannerHref}
            className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-primary text-on-primary font-bold text-sm hover:bg-primary-dim transition-colors whitespace-nowrap"
          >
            Open AI itinerary planner
          </Link>
        </div>
      </div>
    </section>
  );
}

export default PrebuiltItineraryBlog;
